import {
  codeSplit,
  Controller,
  FetchOptions,
  timeoutPromise
} from 'bernie-core';
import { KeyedObject } from 'bernie-core/dist/source';
import {
  AnalyticsStore,
  ContextStore,
  ExperimentStore,
  PageStore
} from 'bernie-plugin-mobx';
import { getTrackingInfoForLogging } from '../common/logging-utils';
import { ActivitySort } from '../common/__generated__/api';
import { EXPERIMENT } from '../constants';
import { handleInvalidDates } from '../helpers/controller-date-helper';
import { isExperimentEnabled } from '../helpers/experiment-helper';
import { ActivitySearchStore, SearchCriteriaStore, SearchQueryObject } from '../stores';

const SERVER_CONTROLLER_FETCH = 15000;
const CLIENT_CONTROLLER_FETCH = 25000;
export class ActivitySearchController implements Controller {
  public pageId = 'page.LX-Search,A,20';
  public path = '/:lang?/things-to-do/search';
  public routeName = 'activity-search';
  public bundles: string[] = [];

  /**
   * Components may be referred to directly
   *   `public component = ExamplePage;`
   *
   * Or, the preferred option is to create a code split point that will generate a new bundle.
   *   Use webpacks magic comments `webpackChunkName: "INSERT_FRIENDLY_NAME_HERE"`
   *   This will give it a friendly name for the name of the file.
   */
  // eslint-disable-next-line import/no-unresolved
  public component = codeSplit(() => import(/* webpackChunkName: "search-page" */ 'views/activity-search-page'));
  public exact = true;

  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('ActivitiesSearch.fetch received invalid FetchOptions');
    }

    const { stores, query } = options;
    const activitySearchStore: ActivitySearchStore = stores.get<ActivitySearchStore>('activitySearchStore');
    const searchCriteriaStore: SearchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const context: ContextStore = stores.get<ContextStore>('context');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const experimentStore: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const page: PageStore = stores.get<PageStore>('page');
    activitySearchStore.query = query;
    activitySearchStore.serverSide = options.isServer;

    searchCriteriaStore.localDateFormat = context.localDateFormat;
    searchCriteriaStore.setExperimentStore(experimentStore);
    this.populateDefaultSortIfApplicable(searchCriteriaStore, query);
    const isQueryKeysDifferent = query && searchCriteriaStore.isQueryKeysDifferent(query);

    const isNewFetchRequired = isQueryKeysDifferent || activitySearchStore.searchError;
    if (isNewFetchRequired) {
      activitySearchStore.retainBrowserFocus = searchCriteriaStore.isSortOrFilterChanged(query);
      // We should reset analytics only for a "new" content refresh being done on client side
      const isDateOrLocationChanged = searchCriteriaStore.isDateOrLocationChanged(query);

      // Bucket User only who applied filter
      if (isDateOrLocationChanged && searchCriteriaStore.isSortOrFilterUpdated(query)) {
        if (experimentStore?.exposures[EXPERIMENT.PRESERVE_FILTERS_41276]) {
          experimentStore.logExperiment(context, page, experimentStore.exposures[EXPERIMENT.PRESERVE_FILTERS_41276]);
        }
        const preserveFilterExperiment = isExperimentEnabled(experimentStore, EXPERIMENT.PRESERVE_FILTERS_41276);
        if (!preserveFilterExperiment) {
          searchCriteriaStore.resetQueryObject(query);
          this.populateDefaultSortIfApplicable(searchCriteriaStore, query);
        }
      }

      if (!options.isServer && analyticsStore) {
        analyticsStore.resetAnalyticsForNewPage();
      }
      searchCriteriaStore.resetSearchCriteriaStore();
      searchCriteriaStore.setContext(context);
      searchCriteriaStore.searchQuery = query as SearchQueryObject;

      searchCriteriaStore.searchQuery = handleInvalidDates(
        query,
        searchCriteriaStore.searchQuery,
        searchCriteriaStore.localDateFormat
      ) as SearchQueryObject;

      activitySearchStore.resetActivitySearchStore();

      if (
        searchCriteriaStore.searchQuery &&
        (searchCriteriaStore.searchQuery.location ||
          searchCriteriaStore.searchQuery.rid ||
          searchCriteriaStore.searchQuery.landingPage)
      ) {
        searchCriteriaStore.populateRegionTypeForLandingPage(query);
        const searchVariables = searchCriteriaStore.searchVariables;

        return timeoutPromise(
          Promise.all([
            activitySearchStore
              .fetchActivitiesSearchDestinationView(
                searchCriteriaStore,
                analyticsStore,
                query,
                searchVariables,
                this.pageId,
                null,
                true
              )
              .then(() => {
                if (activitySearchStore?.searchSummary?.pageInfo?.title) {
                  page.title = activitySearchStore.searchSummary.pageInfo.title;
                }
              })
          ]),
          options.isServer ? SERVER_CONTROLLER_FETCH : CLIENT_CONTROLLER_FETCH,
          `controller=search fetch=page timeout ${getTrackingInfoForLogging(context)}`
        );
      }
    }

    return Promise.resolve();
  }

  private populateDefaultSortIfApplicable(searchCriteriaStore: SearchCriteriaStore, query: KeyedObject): void {
    if (query && !query.sort) {
      query.sort = ActivitySort.RECOMMENDED;
    }
    searchCriteriaStore.populateDefaultSortIfApplicable();
  }

  public fetchPageData(): Promise<{ title: string; pageId: string }> {
    return Promise.resolve({ title: '', pageId: this.pageId });
  }
}
